import { useDispatch, useSelector } from "react-redux";

import useClearAndCloseRowsSelection from "app/pages/sales-intelligence/hooks/useClearAndCloseRowsSelection";
import { ExportCostTypeItem } from "app/pages/sales-intelligence/modules/quota/enums";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import trackingService from "app/pages/sales-intelligence/services/tracking/createMultiSelectorPanelService";
import { selectExcelQuotaRemaining } from "app/pages/sales-intelligence/sub-modules/common/store/selectors";
import { LeadsType } from "app/pages/sales-intelligence/sub-modules/company-search/types/common";
import LegacyWebsitesCRMExportPanelView from "app/pages/sales-intelligence/sub-modules/management/components/LegacyWebsitesCRMExportPanel/LegacyWebsitesCRMExportPanelView";
import WebsitesCRMExportPanelView from "app/pages/sales-intelligence/sub-modules/management/components/WebsitesCRMExportPanel/WebsitesCRMExportPanelView";
import { exportDomainsToSFThunk } from "app/pages/sales-intelligence/sub-modules/management/store/effects/salesforce/export";
import { selectExportingToSF } from "app/pages/sales-intelligence/sub-modules/management/store/selectors/salesforce/exportSelectors";
import type { BaseActivatedPanelProps } from "app/pages/sales-intelligence/sub-modules/table-actions-panel/components/BaseActivatedPanel/types";
import type { SelectionDropdownItem } from "app/pages/sales-intelligence/sub-modules/table-actions-panel/models/selection-dropdown-item/types";
import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";

import ManualSelectionItem from "../../../../table-actions-panel/models/selection-dropdown-item/ManualSelectionItem/ManualSelectionItem";
import { COMPETITIVE_LANDSCAPE_TABLE_KEY, COMPETITIVE_LANDSCAPE_TABLE_PROPERTY } from "../../configuration/constants";
import useCompetitiveQueryParams from "../../hooks/useCompetitiveQueryParams";
import crmExportSelectionDropdown from "../../models/crm-export/crmExportSelectionDropdown";
import { tableRowsSelectionToggledAction } from "../../store/action-creators";
import { getTopDomainsCompetitiveThunk } from "../../store/effects";
import { selectCheckedWebsites, selectCompetitiveResultsCount, selectIsFetchingResults } from "../../store/selectors";

const SalesforceCompetitiveExportPanelContainer = (props: BaseActivatedPanelProps) => {
    const dispatch = useDispatch();
    const excelQuotaRemaining = useSelector(selectExcelQuotaRemaining);
    const numberOfResults = useSelector(selectCompetitiveResultsCount);
    const isFetchingResults = useSelector(selectIsFetchingResults);
    const selectedWebsites = useSelector(selectCheckedWebsites);
    const isExportInProgress = useSelector(selectExportingToSF);
    const clearAndCloseRowsSelection = useClearAndCloseRowsSelection(
        tableRowsSelectionToggledAction,
        COMPETITIVE_LANDSCAPE_TABLE_KEY,
        COMPETITIVE_LANDSCAPE_TABLE_PROPERTY,
    );

    const { params } = useCompetitiveQueryParams();

    const handleDeactivate = () => {
        clearAndCloseRowsSelection();
        props.onDeactivate();
    };

    const handleSelectionChange = (item: Readonly<SelectionDropdownItem>) => {
        if (item.id === ManualSelectionItem.ID) {
            return dispatch(tableRowsSelectionToggledAction(true));
        }

        clearAndCloseRowsSelection();
    };

    async function handleSubmit(item: Readonly<SelectionDropdownItem>) {
        trackingService.trackMultiSelectorExportClick("salesforce", excelQuotaRemaining, item.alias, item.value);

        if (item.id === ManualSelectionItem.ID) {
            handleDeactivate();
            return dispatch(exportDomainsToSFThunk(selectedWebsites, [WORLDWIDE_COUNTRY_ID], LeadsType.Websites));
        }

        const websites = await dispatch(getTopDomainsCompetitiveThunk(params, item.value));

        if (Array.isArray(websites)) {
            handleDeactivate();
            dispatch(exportDomainsToSFThunk(websites, [WORLDWIDE_COUNTRY_ID], LeadsType.Websites));
        }
    }

    if (salesSettingsHelper.isDataCreditsEnabled) {
        return (
            <WebsitesCRMExportPanelView
                isDisabled={isExportInProgress || isFetchingResults}
                isLoading={isExportInProgress || isFetchingResults}
                label={{ key: "si.multi_selector.salesforce.title" }}
                numberOfSelectedItems={selectedWebsites.length}
                numberOfRemainingExports={excelQuotaRemaining}
                numberOfResults={numberOfResults ?? 0}
                selectionDropdown={crmExportSelectionDropdown}
                onDeactivate={handleDeactivate}
                onSelectionChange={handleSelectionChange}
                onSubmit={handleSubmit}
                exportType={ExportCostTypeItem.Salesforce}
            />
        );
    }

    return (
        <LegacyWebsitesCRMExportPanelView
            isDisabled={isExportInProgress || isFetchingResults}
            isLoading={isExportInProgress || isFetchingResults}
            label={{ key: "si.multi_selector.salesforce.title" }}
            numberOfSelectedItems={selectedWebsites.length}
            numberOfRemainingExports={excelQuotaRemaining}
            numberOfResults={numberOfResults ?? 0}
            selectionDropdown={crmExportSelectionDropdown}
            onDeactivate={handleDeactivate}
            onSelectionChange={handleSelectionChange}
            onSubmit={handleSubmit}
        />
    );
};

export default SalesforceCompetitiveExportPanelContainer;
